/* Draws the cards that talk to an app's endpoints: the App Form Card, the App Calendar Card and the App Alert Card's optional text method.
   Rules, wording and field lists come from the card's values and the app's endpoints; this file holds none.
   Looks come from the layered tokens (rfhq_templates.css). Text is set with textContent; links must be paths on
   this site; method names are checked by rfhqUI.call. */
(function () {
	const UI = window.rfhqUI;
	if (!UI) return;
	const { h, call, localPath } = UI;

	function urlValues(names) {
		const out = {}, query = new URLSearchParams(window.location.search);
		(names || "").split(",").map((n) => n.trim()).filter(Boolean).forEach((n) => { if (query.has(n)) out[n] = query.get(n); });
		return out;
	}
	const alertBox = (kind, text) => h("div", { class: "rfhq-alert rfhq-alert--" + kind, role: kind === "danger" ? "alert" : "status", text });

	/* ---------------- App Form Card ---------------- */
	function formCard(root) {
		let cfg;
		try { cfg = JSON.parse(root.dataset.config); } catch (e) { return; }
		const banner = root.querySelector(".rfhq-form-banner");
		const form = root.querySelector("form");
		const json = (value, fallback) => { if (typeof value !== "string") return value || fallback; try { return JSON.parse(value) || fallback; } catch (e) { return fallback; } };
		const args = Object.assign({}, json(cfg.static_args, {}), urlValues(cfg.url_args));
		const controls = {};
		let group = null, fields = json(cfg.fields, []), submitLabel = cfg.submit_label || "";
		// A save may answer with the next step of the same form ({next: {fields, submit_label, save, args, title, subtitle, notice, consent, skip, again}}): the form is drawn again for it, and what the app
		// handed back (args, and the csrf token of a session the step has just started) goes with the next save. A form that never answers so is unchanged.
		let saveMethod = cfg.save, csrf = "", extra = {}, head = null, consent = [], step = null, timer = null, rowGroup = null, moreLabel = "", addMore = null;

		function build() {
			form.replaceChildren();
			Object.keys(controls).forEach((k) => delete controls[k]);
			group = null; rowGroup = null; moreLabel = ""; addMore = null;
			fields.forEach((field) => {
				const id = "rfhq-f-" + field.name;
				let input;
				if (field.type === "select") {
					// An option may carry `fills` ({field: value}): choosing it fills those fields (a saved person's details). A select that names `controls` (fields) hides them while a saved one is chosen,
					// and brings them back, emptied, for "none of these" (the placeholder), where the person writes new ones.
					input = h("select", { id, name: field.name, class: "form-control" }, h("option", { value: "", text: field.placeholder || "" }),
						...(field.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o)).map((o) => h("option", { value: o.value, text: o.label })));
				} else if (field.type === "textarea") {
					input = h("textarea", { id, name: field.name, class: "form-control", rows: "3", placeholder: field.placeholder || "" });
				} else if (field.type === "checkbox") {
					input = h("input", { id, name: field.name, type: "checkbox" });
				} else if (field.type === "lookup") {
					// Type to search: an app's method answers [{value, label}] for what was typed; the value chosen is what is sent (with `free_text`, what was typed when it is not on the list).
					const list = h("datalist", { id: id + "-list" });
					input = h("input", { id, name: field.name, class: "form-control", type: "text", list: id + "-list", placeholder: field.placeholder || "", autocomplete: "off" });
					input.__known = {}; input.__list = list;
					let timer = null;
					input.addEventListener("input", () => {
						clearTimeout(timer);
						const query = input.value.trim();
						if (query.length < 2) return;
						timer = setTimeout(async () => {
							const result = await call(field.source, { query });
							const rows = (result.ok && Array.isArray(result.data) && result.data) || [];
							rows.forEach((r) => { input.__known[r.label] = r.value; });
							list.replaceChildren(...rows.map((r) => h("option", { value: r.label })));
						}, 250);
					});
					input.__holder = list;
				} else if (field.type === "file") {
					input = h("input", { id, name: field.name, type: "file", class: "form-control", accept: (field.accept || []).map((e) => "." + e).join(",") });
				} else if (field.type === "password") {
					input = h("input", { id, name: field.name, class: "form-control", type: "password", placeholder: field.placeholder || "", autocomplete: field.autocomplete || "new-password" });
					const show = h("button", { type: "button", class: "rfhq-show-secret", text: field.show_label || "Show" });
					show.addEventListener("click", () => { const reveal = input.type === "password"; input.type = reveal ? "text" : "password"; show.textContent = reveal ? (field.hide_label || "Hide") : (field.show_label || "Show"); });
					input.__holder = show;
				} else {
					input = h("input", { id, name: field.name, class: "form-control", type: field.type === "date" ? "date" : field.type === "time_text" ? "time" : field.type === "tel" ? "tel" : "text",
						placeholder: field.placeholder || "", autocomplete: field.autocomplete || "off", inputmode: field.input_mode || (field.type === "tel" ? "tel" : null), maxlength: field.max_length || null });  // a time is picked or typed in the browser's own time control (a numeric keypad has no colon)
				}
				const error = h("p", { class: "rfhq-form-error", role: "alert", hidden: true });
				const label = h("label", { for: id, class: "control-label", text: field.label + (field.required ? " *" : "") });
				const row = h("div", { class: "form-group" + (field.type === "checkbox" ? " checkbox" : "") + (field.type === "password" ? " rfhq-secret" : "") }, field.type === "checkbox" ? [h("label", { for: id }, input, " ", field.label)] : [label, input, input.__list || null, field.type === "password" ? input.__holder : null],
					field.hint ? h("p", { class: "help-box", text: field.hint }) : null, error);
				controls[field.name] = { field, input, error, row };
				if (field.width) row.classList.add("rfhq-field-" + field.width);  // "narrow" or "wide": how much of a shared row this field takes
				// A field may sit in a group (a box with a heading) and in a row of that box (fields side by side, a country code beside the mobile number).
				let target = form;
				if (field.group) {
					if (!group || group.name !== field.group) {
						group = { name: field.group, el: h("fieldset", { class: "rfhq-form-group" }, h("legend", { text: field.group })) };
						if (field.more) {
							// A group marked `more` stays out of sight until it has something in it or the person asks for another (the button below the groups); its ✕ empties and hides it again.
							group.el.classList.add("rfhq-group-more");
							const remove = h("button", { type: "button", class: "rfhq-group-remove", "aria-label": field.remove_label || "Remove", text: "\u2715" });
							remove.addEventListener("click", () => { group.el.querySelectorAll("input, textarea, select").forEach((i) => { i.value = ""; }); delete group.el.dataset.shown; syncMore(); });
							group.el.querySelector("legend").append(remove);
							if (field.more_label) moreLabel = field.more_label;
						}
						form.append(group.el);
					}
					target = group.el;
				} else { group = null; }
				if (field.row) {
					if (!rowGroup || rowGroup.name !== field.row || rowGroup.parent !== target) { rowGroup = { name: field.row, parent: target, el: h("div", { class: "rfhq-form-row" }) }; target.append(rowGroup.el); }
					rowGroup.el.append(row);
				} else { rowGroup = null; target.append(row); }
				if (field.default != null) setValue(field.name, field.default);
			});
			if (moreLabel) {
				addMore = h("button", { type: "button", class: "rfhq-add-more", text: "+ " + moreLabel });
				addMore.addEventListener("click", () => {
					const next = form.querySelector(".rfhq-group-more[hidden]");
					if (!next) return;
					next.dataset.shown = "1"; syncMore();
					const first = next.querySelector("input, textarea, select"); if (first) first.focus();
				});
				form.append(addMore);
			}
			fields.forEach((f) => { if (f.type === "select" && (f.options || []).some((o) => o && o.fills)) pick(f.name, false); });
			if (consent.length) {
				// The words under the fields: plain text and links to pages on this site (the app's wording).
				form.append(h("p", { class: "rfhq-form-consent" }, ...consent.map((part) => part.href && localPath(part.href)
					? h("a", { href: localPath(part.href), target: "_blank", rel: "noopener", text: part.label }) : document.createTextNode(part.text || part.label || ""))));
			}
			const submit = h("button", { type: "submit", class: "rfhq-cta-primary", text: submitLabel });
			const links = [];
			if (step && step.again) links.push(againButton(step.again));
			if (step && step.skip && localPath(step.skip.redirect)) links.push(h("a", { class: "rfhq-cta-secondary", href: localPath(step.skip.redirect), text: step.skip.label || "" }));
			form.append(h("div", { class: "rfhq-form-actions" }, submit, ...links,
				!step && cfg.cancel_url ? h("a", { class: "rfhq-cta-secondary", href: localPath(cfg.cancel_url) || "#", text: cfg.cancel_label || "" }) : null));
			form.onsubmit = (event) => { event.preventDefault(); send(submit); };
			form.onchange = (event) => { const c = event.target && controls[event.target.name]; if (c && c.field.type === "select" && (c.field.options || []).some((o) => o && o.fills)) pick(c.field.name, true); applySwitches(); };
			applySwitches();
		}
		// "Send again": asks the app for the step afresh (a new code), after the wait the app names.
		function againButton(spec) {
			const button = h("button", { type: "button", class: "rfhq-cta-secondary", text: spec.label || "" });
			let left = Number(spec.wait) || 0;
			const tick = () => { button.disabled = left > 0; button.textContent = left > 0 ? (spec.waiting || spec.label || "").replace("{0}", String(left)) : (spec.label || ""); if (left > 0) left -= 1; else { clearInterval(timer); timer = null; } };
			clearInterval(timer); timer = null; tick(); if (left > 0) timer = setInterval(tick, 1000);
			button.addEventListener("click", async () => {
				button.disabled = true;
				const result = await call(spec.save, Object.assign({}, args, spec.args || {}), { csrf });
				if (!result.ok) { button.disabled = false; banner.replaceChildren(alertBox("danger", result.message || "")); return; }
				const data = result.data || {};
				if (data.next) applyStep(data.next); else { button.disabled = false; banner.replaceChildren(alertBox("danger", data.message || "")); }
			});
			return button;
		}
		function applyStep(next) {
			step = next;
			fields = next.fields || [];
			submitLabel = next.submit_label || submitLabel;
			saveMethod = next.save || saveMethod;
			extra = next.args || {};
			consent = next.consent || [];
			if (head) { head.remove(); head = null; }
			if (next.title) {
				head = h("div", { class: "rfhq-form-head" }, h("h2", { class: "rfhq-form-title", text: next.title }), next.subtitle ? h("p", { class: "help-box", text: next.subtitle }) : null);
				root.insertBefore(head, root.firstChild);
			}
			banner.replaceChildren(next.notice && next.notice.text ? alertBox(["success", "warning", "danger"].includes(next.notice.kind) ? next.notice.kind : "info", next.notice.text) : "");
			build();
			const first = form.querySelector("input, select, textarea");
			if (first) first.focus();
		}
		function getValue(name) {
			const { field, input } = controls[name];
			if (field.type === "file") return (input.files && input.files[0]) || "";
			if (field.type === "lookup") { const typed = input.value.trim(); return typed ? (input.__known[typed] || (field.free_text ? typed : "")) : ""; }  // free_text: what was typed is sent when it is not on the list (the app then reads it)
			return field.type === "checkbox" ? (input.checked ? 1 : 0) : field.type === "password" ? input.value : input.value.trim();  // a password keeps its spaces
		}
		function setValue(name, value) {
			const { field, input } = controls[name];
			if (field.type === "checkbox") input.checked = !!Number(value) || value === true;
			else if (field.type === "lookup") {
				// A loaded value may be {value, label} so the person sees a name, not a code.
				if (value && typeof value === "object") { input.__known[value.label] = value.value; input.value = value.label || ""; } else { input.value = field.free_text && value != null ? String(value) : ""; }
			} else if (field.type !== "file") input.value = value == null ? "" : String(value);
		}
		// the chosen option of a select that fills other fields: its values go into them; "none of these" empties them (back to their own default) so that new ones are written
		function pick(name, changed) {
			const { field } = controls[name], value = getValue(name);
			const option = (field.options || []).find((o) => o && String(o.value) === String(value));
			if (value && option && option.fills) Object.entries(option.fills).forEach(([n, v]) => { if (controls[n]) setValue(n, v); });
			else if (!value && changed) (field.controls || []).forEach((n) => { if (controls[n]) setValue(n, controls[n].field.default != null ? controls[n].field.default : ""); });
			(field.controls || []).forEach((n) => { if (controls[n]) controls[n].row.hidden = !!value; });
		}
		// the groups marked `more`: shown when they hold something or were asked for; the add button goes when none is left to show
		function syncMore() {
			let hidden = false;
			form.querySelectorAll(".rfhq-group-more").forEach((g) => {
				if ([...g.querySelectorAll("input, textarea, select")].some((i) => i.value && i.value.trim())) g.dataset.shown = "1";
				g.hidden = !g.dataset.shown;
				hidden = hidden || g.hidden;
			});
			if (addMore) addMore.hidden = !hidden;
		}
		function applySwitches() {
			syncMore();
			Object.values(controls).forEach(({ field, input, row }) => {
				if (!field.disabled_when || !controls[field.disabled_when]) return;
				const off = !!getValue(field.disabled_when);
				input.disabled = off;
				row.classList.toggle("rfhq-field-disabled", off);
				if (off) setValue(field.name, field.type === "checkbox" ? 0 : "");
			});
		}
		function showErrors(errors) {
			Object.values(controls).forEach(({ error, input }) => { error.hidden = true; input.removeAttribute("aria-invalid"); });
			let first = null;
			Object.entries(errors || {}).forEach(([name, text]) => {
				const c = controls[name];
				if (!c) return;
				c.error.textContent = text; c.error.hidden = false; c.input.setAttribute("aria-invalid", "true");
				first = first || c.input;
			});
			if (first) first.focus();
			return !!first;
		}
		async function upload(file, field) {
			const ending = (file.name.split(".").pop() || "").toLowerCase();
			if (!(field.accept || []).includes(ending)) return { error: "Choose a file of type: " + (field.accept || []).join(", ") + "." };
			if (file.size > (field.max_mb || 10) * 1024 * 1024) return { error: "The file must be smaller than " + (field.max_mb || 10) + " MB." };
			const body = new FormData();
			body.append("file", file, file.name);
			body.append("is_private", "1");
			try {
				const response = await fetch("/api/method/upload_file", { method: "POST", credentials: "same-origin", headers: { "X-Frappe-CSRF-Token": (window.frappe && frappe.csrf_token) || "" }, body });
				const data = await response.json().catch(() => ({}));
				return response.ok && data.message && data.message.file_url ? { url: data.message.file_url } : { error: "The file could not be uploaded." };
			} catch (e) { return { error: "The file could not be uploaded." }; }
		}
		async function send(submit) {
			banner.replaceChildren();
			const values = {}, missing = {};
			fields.forEach(({ name, required, label }) => {
				const c = controls[name];
				values[name] = c.input.disabled ? (c.field.type === "checkbox" ? 0 : "") : getValue(name);
				if (required && !c.input.disabled && !values[name]) missing[name] = label + " is required.";
				if (c.field.type === "lookup" && !c.input.disabled && c.input.value.trim() && !values[name]) missing[name] = c.field.not_found || "Choose one from the list.";
			});
			if (Object.keys(missing).length) { showErrors(missing); return; }
			showErrors({});
			submit.disabled = true;
			for (const { name } of fields) {
				const c = controls[name];
				if (c.field.type !== "file" || !values[name]) continue;
				const sent = await upload(values[name], c.field);
				if (sent.error) { submit.disabled = false; showErrors({ [name]: sent.error }); return; }
				values[name] = sent.url;
			}
			const result = await call(saveMethod, Object.assign({}, args, extra, { values }), { csrf });
			submit.disabled = false;
			if (!result.ok) { banner.replaceChildren(alertBox("danger", result.message || "")); return; }
			const data = result.data || {};
			if (data.csrf_token) csrf = data.csrf_token;  // a step that signed the person in: what follows is asked as them
			if (data.ok === false) {
				if (!showErrors(data.errors) || data.message) banner.replaceChildren(alertBox("danger", data.message || ""));
				return;
			}
			if (data.next) { applyStep(data.next); return; }
			const go = localPath(data.redirect) || localPath(cfg.success_redirect);
			if (go) { window.location.href = go; return; }
			banner.replaceChildren(alertBox("success", data.message || cfg.success_message || ""));
		}

		async function start() {
			if (cfg.fields_source) {
				const result = await call(cfg.fields_source, args);
				if (!result.ok) { banner.replaceChildren(alertBox("danger", result.message || "")); return; }
				fields = (result.data && result.data.fields) || [];
				submitLabel = (result.data && result.data.submit_label) || submitLabel;  // the app may word the button for how this one is paid
				const answer = result.data || {};
				if (!fields.length && !(answer.notice && answer.notice.text) && !(answer.actions || []).length) { root.hidden = true; return; }  // nothing to ask
				if (result.data && result.data.title) {
					head = h("div", { class: "rfhq-form-head" }, h("h2", { class: "rfhq-form-title", text: result.data.title }),
						result.data.subtitle ? h("p", { class: "help-box", text: result.data.subtitle }) : null);
					root.insertBefore(head, root.firstChild);
				}
				const data = result.data || {};
				consent = data.consent || [];
				if (data.notice && data.notice.text) banner.replaceChildren(alertBox(["success", "warning", "danger"].includes(data.notice.kind) ? data.notice.kind : "info", data.notice.text));
				if (!fields.length) {
					// Nothing to ask (for example the person must first add something else): say so, offer the way forward.
					form.replaceChildren(h("div", { class: "rfhq-form-actions" }, ...(data.actions || []).filter((a) => localPath(a.href)).map((a) =>
						h("a", { class: a.kind === "primary" ? "rfhq-cta-primary" : "rfhq-cta-secondary", href: localPath(a.href), text: a.label }))));
					return;
				}
				build();
				const first = (result.data && result.data.values) || {};
				Object.keys(controls).forEach((n) => { if (n in first) setValue(n, first[n]); });
				applySwitches();
			} else { build(); }
			if (cfg.load) {
				const result = await call(cfg.load, args);
				if (!result.ok) { banner.replaceChildren(alertBox("danger", result.message || "")); return; }
				const loaded = (result.data && result.data.values) || {};
				Object.keys(controls).forEach((n) => { if (n in loaded) setValue(n, loaded[n]); });
				applySwitches();
			}
		}
		start();
	}

	/* ---------------- App Alert Card (text from an endpoint) ---------------- */
	async function alertCard(el) {
		const result = await call(el.dataset.source, urlValues(el.dataset.urlArgs));
		const data = result.ok && result.data;
		if (!data || !data.text) { el.hidden = true; return; }
		el.className = "rfhq-alert rfhq-alert--" + (["success", "warning", "danger"].includes(data.kind) ? data.kind : "info");
		el.textContent = data.text;
		el.hidden = false;
	}

	/* ---------------- App Calendar Card ---------------- */
	const pad = (n) => String(n).padStart(2, "0");
	const isoDay = (d) => d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
	function calendarCard(root) {
		let cfg;
		try { cfg = JSON.parse(root.dataset.config); } catch (e) { return; }
		const banner = root.querySelector(".rfhq-cal-banner"), head = root.querySelector(".rfhq-cal-head"), grid = root.querySelector(".rfhq-cal-grid"), legend = root.querySelector(".rfhq-cal-legend");
		const fixed = (() => { try { return JSON.parse(cfg.static_args) || {}; } catch (e) { return {}; } })();
		const args = Object.assign({}, fixed, urlValues(cfg.url_args));
		const tones = ["success", "warning", "danger", "info"];
		let month = new Date(); month.setDate(1);
		const monthKey = () => month.getFullYear() + "-" + pad(month.getMonth() + 1);

		async function load() {
			const result = await call(cfg.source, Object.assign({}, args, { month: monthKey() }));
			banner.replaceChildren();
			if (!result.ok) { banner.replaceChildren(alertBox("danger", result.message || "")); return; }
			draw(result.data || {});
		}
		function move(step) { month = new Date(month.getFullYear(), month.getMonth() + step, 1); load(); }

		function draw(data) {
			const entries = {};
			(data.entries || []).forEach((e) => { (entries[e.date] = entries[e.date] || []).push(e); });
			const title = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
			head.replaceChildren(
				...[h("button", { type: "button", class: "rfhq-cal-nav", "aria-label": cfg.prev_label || "", text: "\u2039", onclick: () => move(-1) }),
				h("strong", { class: "rfhq-cal-title", text: title }),
				h("button", { type: "button", class: "rfhq-cal-nav", "aria-label": cfg.next_label || "", text: "\u203a", onclick: () => move(1) }),
				cfg.today_label ? h("button", { type: "button", class: "rfhq-cal-today", text: cfg.today_label, onclick: () => { month = new Date(); month.setDate(1); load(); } }) : null].filter(Boolean));
			const first = new Date(month.getFullYear(), month.getMonth(), 1), start = new Date(first);
			start.setDate(1 - ((first.getDay() + 6) % 7));  // weeks start on Monday
			const cells = [];
			for (let i = 0; i < 7; i++) { const d = new Date(start); d.setDate(start.getDate() + i); cells.push(h("div", { class: "rfhq-cal-dow", role: "columnheader", text: d.toLocaleDateString(undefined, { weekday: "short" }) })); }
			const today = isoDay(new Date());
			for (let i = 0; i < 42; i++) {
				const d = new Date(start); d.setDate(start.getDate() + i);
				if (i >= 35 && d.getMonth() !== month.getMonth()) break;
				const key = isoDay(d), mine = entries[key] || [], outside = d.getMonth() !== month.getMonth();
				const add = data.add_href && localPath(data.add_href) ? localPath(data.add_href) + (data.add_href.includes("?") ? "&" : "?") + "date=" + key : null;
				const day = h("div", { class: "rfhq-cal-day" + (outside ? " is-outside" : "") + (key === today ? " is-today" : "") + (mine.length ? " has-entries" : ""), role: "gridcell" },
					add && !outside ? h("a", { class: "rfhq-cal-num", href: add, "aria-label": d.toLocaleDateString(undefined, { dateStyle: "full" }), text: String(d.getDate()) }) : h("span", { class: "rfhq-cal-num", text: String(d.getDate()) }),
					...mine.map((e) => {
						const cls = "rfhq-cal-entry" + (tones.includes(e.tone) ? " rfhq-cal-entry--" + e.tone : "");
						const label = (e.time ? e.time + " " : "") + (e.title || "");
						const link = e.href && localPath(e.href);
						return link ? h("a", { class: cls, href: link, title: label, text: label }) : h("span", { class: cls, title: label, text: label });
					}));
				cells.push(day);
			}
			grid.replaceChildren(...cells);
			legend.replaceChildren(...(data.legend || []).filter((l) => l && l.label).map((l) => h("span", { class: "rfhq-cal-key" + (tones.includes(l.tone) ? " rfhq-cal-entry--" + l.tone : ""), text: l.label })));
		}
		load();
	}

	function init() {
		document.querySelectorAll(".rfhq-form-card:not([data-mounted])").forEach((el) => { el.setAttribute("data-mounted", "1"); formCard(el); });
		document.querySelectorAll(".rfhq-calendar-card:not([data-mounted])").forEach((el) => { el.setAttribute("data-mounted", "1"); calendarCard(el); });
		document.querySelectorAll(".rfhq-alert[data-source]:not([data-mounted])").forEach((el) => { el.setAttribute("data-mounted", "1"); alertCard(el); });
	}
	if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init); else init();
})();
